import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import useAutoAudioRecorder from "../../hooks/useAutoAudioRecorder";
import {
  Clock, Mic, MicOff, Volume2, Code2,
  CheckCircle2, AlertCircle, X, Send,
  LogOut, MessageSquare, ShieldCheck, Bot, PhoneOff,
  CheckCircle, ChevronRight,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppBadge,
  ProgressBar, Separator, UserAvatar,
} from "../ui";
import "./mock-interview.scss";

/* ---------- sub-components ---------- */

/* hook: get available voices */
const useVoices = () => {
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    if ("speechSynthesis" in window) {
      const update = () => setVoices(window.speechSynthesis.getVoices());
      window.speechSynthesis.onvoiceschanged = update;
      update();
    }
  }, []);
  return voices;
};

/* hook: get active microphone label */
const useMicLabel = () => {
  const [micLabel, setMicLabel] = useState("");
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices?.().then((devices) => {
      const mic = devices.find((d) => d.kind === "audioinput" && d.deviceId === "default")
        || devices.find((d) => d.kind === "audioinput");
      if (mic) setMicLabel(mic.label || "Microphone");
    }).catch(() => {});
  }, []);
  return micLabel;
};

function formatTime(secs) {
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
}

/* ---------- Highlighted question text ---------- */
const HighlightedText = ({ text, highlightIndex }) => {
  if (highlightIndex < 0 || !text) {
    return <span>{text}</span>;
  }
  // Split text into words preserving whitespace
  const words = text.split(/(\s+)/);
  let wordCount = 0;
  return (
    <span>
      {words.map((segment, i) => {
        if (/^\s+$/.test(segment)) return <span key={i}>{segment}</span>;
        const idx = wordCount++;
        return (
          <span
            key={i}
            className={idx === highlightIndex ? "mis-word--active" : "mis-word"}
          >
            {segment}
          </span>
        );
      })}
    </span>
  );
};

const thinkingMessages = [
  "Hmm, let me think about that...",
  "Interesting, let me consider your response...",
  "Reviewing your answer...",
  "Preparing the next question...",
  "Analyzing your approach...",
  "Noting your response...",
  "Good, moving on...",
];

/* ---------- Helpers for phase-based status text ---------- */
function getAiPanelStatus(phase) {
  if (phase === "ai-speaking") return "Speaking...";
  if (phase === "listening") return "Listening to your answer";
  if (phase === "confirm-transcript") return "Review your answer";
  if (phase === "preparing-next") return "Thinking...";
  if (phase === "updating-answer" || phase === "waiting-feedback") return "Processing...";
  if (phase === "complete") return "Interview ended";
  return "Ready";
}

function getUserMicStatus(isMuted, phase) {
  if (isMuted) return "Microphone muted";
  if (phase === "listening") return "Microphone on — speak now";
  return "Microphone on";
}

function getScoreClass(score) {
  if (score >= 70) return " mis-session-feedback__score--high";
  if (score >= 40) return " mis-session-feedback__score--mid";
  return " mis-session-feedback__score--low";
}

/* ---------- main component ---------- */

const MockInterviewSession = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { mockInterviewId } = useParams();
  const voices = useVoices();
  const micLabel = useMicLabel();
  const currentUser = getCurrentUser();

  const durationMinutes = state?.duration || 10;
  const totalSeconds = durationMinutes * 60;
  const sessionTitle = state?.technology
    ? `${state.technology} ${state.interviewType || "Technical"}`
    : "Mock Interview";

  const [phase, setPhase] = useState("ai-speaking");
  const [interviewEnded, setInterviewEnded] = useState(false);
  const [question, setQuestion] = useState(
    typeof state?.firstQuestion === "object"
      ? state.firstQuestion
      : { text: state?.firstQuestion || "", needsCode: state?.needsCode || false }
  );
  const maxQuestions = (durationMinutes || 10) * 2;
  const [progress, setProgress] = useState({ current: 1, total: maxQuestions });
  const [answer, setAnswer] = useState("");
  const [code, setCode] = useState("// Write your code here\n");
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState("");
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [highlightWordIndex, setHighlightWordIndex] = useState(-1);
  const selectedVoice = voices.find((v) => v.lang.startsWith("en")) || voices[0];
  const [questionAudioBase64, setQuestionAudioBase64] = useState(state?.questionAudioBase64 || null);
  const audioPlayerRef = useRef(null);
  const initialQuestionSpokenRef = useRef(false);

  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
  const [timeUp, setTimeUp] = useState(false);
  const timerRef = useRef();

  /* New state for Replit-style UI */
  const [isMuted, setIsMuted] = useState(false);
  const [showQAPanel, setShowQAPanel] = useState(true);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [transcript, setTranscript] = useState("");
  const [thinkingMsg, setThinkingMsg] = useState("");

  /* --- check if interview already completed on mount (back-nav guard) --- */
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await apiClient.get(`/mock-interview/${mockInterviewId}`);
        if (data?.status === "completed") {
          setInterviewEnded(true);
          setPhase("complete");
        }
      } catch {}
    };
    if (mockInterviewId) checkStatus();
  }, [mockInterviewId]);

  /* --- replace history on complete so back button can't return --- */
  useEffect(() => {
    if (phase === "complete" || interviewEnded) {
      window.history.replaceState(null, "", window.location.href);
    }
  }, [phase, interviewEnded]);

  /* --- timer --- */
  useEffect(() => {
    if (timeUp || phase === "complete" || interviewEnded) return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { setTimeUp(true); clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timeUp, phase, interviewEnded]);

  /* --- speak FIRST question on mount --- */
  useEffect(() => {
    if (initialQuestionSpokenRef.current) return;
    if (!question?.text || timeUp || interviewEnded) return;
    initialQuestionSpokenRef.current = true;
    setPhase("ai-speaking");
    speakText(question.text, () => {
      setHighlightWordIndex(-1);
      setPhase("listening");
    });
    // eslint-disable-next-line
  }, [question.text]);

  useEffect(() => {
    setShowCodeModal(phase === "waiting-code");
  }, [phase]);

  function speakText(text, onEnd, audioBase64) {
    // Stop any previous audio
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    window.speechSynthesis?.cancel();

    // Use OpenAI TTS audio if available
    const b64 = audioBase64 || questionAudioBase64;
    if (b64) {
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioPlayerRef.current = audio;

      audio.onended = () => {
        setHighlightWordIndex(-1);
        audioPlayerRef.current = null;
        window.dispatchEvent(new CustomEvent('mock-interview-done-speaking'));
        onEnd?.();
      };
      audio.onerror = () => {
        // Fallback to browser TTS on audio error
        audioPlayerRef.current = null;
        speakWithBrowserTTS(text, onEnd);
      };

      // Word highlighting estimate based on typical speech rate
      const words = text.split(/\s+/).filter(Boolean);
      setHighlightWordIndex(0);
      audio.play().then(() => {
        const estimatedDuration = audio.duration || words.length * 0.35;
        const wordInterval = (estimatedDuration / words.length) * 1000;
        words.forEach((_, i) => {
          setTimeout(() => setHighlightWordIndex(i), i * wordInterval);
        });
      }).catch(() => speakWithBrowserTTS(text, onEnd));
      // Clear the stored base64 after use
      setQuestionAudioBase64(null);
      return;
    }

    // Fallback: browser TTS
    speakWithBrowserTTS(text, onEnd);
  }

  function speakWithBrowserTTS(text, onEnd) {
    if (!window.speechSynthesis) { onEnd?.(); return; }
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    if (selectedVoice) u.voice = selectedVoice;

    u.onboundary = (event) => {
      if (event.name === "word") {
        const spoken = text.substring(0, event.charIndex);
        const spokenWords = spoken.split(/\s+/).filter(Boolean);
        setHighlightWordIndex(spokenWords.length);
      }
    };
    u.onend = () => {
      setHighlightWordIndex(-1);
      window.dispatchEvent(new CustomEvent('mock-interview-done-speaking'));
      onEnd?.();
    };
    setHighlightWordIndex(0);
    window.speechSynthesis.speak(u);
  }

  /* --- audio recorder --- */
  useAutoAudioRecorder({
    enabled: phase === "listening" && !interviewEnded && !isMuted,
    mockInterviewId,
    onTranscribed: async (data) => {
      if (data.error) { setError(data.error); setPhase("ai-speaking"); return; }
      // Show thinking indicator immediately when recording stops
      if (data._thinking) {
        setThinkingMsg(thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)]);
        setPhase("preparing-next");
        return;
      }
      // /answer-audio returns full response (transcript + feedback + nextQuestion)
      handleApiResponse(data);
    },
  });

  // Submit confirmed transcript as text answer
  const submitConfirmedTranscript = async () => {
    setPhase("updating-answer");
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.post("/mock-interview/answer", {
        mockInterviewId,
        answer: transcript,
      });
      handleApiResponse(data);
    } catch { setError("Failed to submit answer."); setPhase("listening"); }
    setLoading(false);
  };

  // Re-record: go back to listening
  const handleReRecord = () => {
    setTranscript("");
    setPhase("listening");
  };

  function handleApiResponse(data) {
    setAnswer(data.transcript || "");
    // Store TTS audio for next question
    if (data.questionAudioBase64) {
      setQuestionAudioBase64(data.questionAudioBase64);
    }
    if (data.needsCode && data.nextQuestion) {
      setCode("// Write your code here\n");
      const next = typeof data.nextQuestion === "object"
        ? data.nextQuestion
        : { text: data.nextQuestion || "", needsCode: data.needsCode || false };
      setQuestion(next);
      setTimeout(() => speakText(data.nextQuestion, () => setPhase("waiting-code"), data.questionAudioBase64), 300);
      return;
    }
    if (data.interviewComplete) {
      // Speak a closing thank you before redirecting
      const closingMsg = "Thank you for your time! That concludes our interview. Let me prepare your summary.";
      setPhase("ai-speaking");
      speakText(closingMsg, () => {
        setPhase("complete");
        setInterviewEnded(true);
        setTimeout(() => navigate(`/s/mock-interview/summary/${mockInterviewId}`, { replace: true }), 1500);
      });
      return;
    }
    const next = typeof data.nextQuestion === "object"
      ? data.nextQuestion
      : { text: data.nextQuestion || "", needsCode: data.needsCode || false };
    setQuestion(next);
    setProgress((p) => ({ ...p, current: p.current + 1 }));
    // Explicitly trigger speaking for the next question
    setAnswer(""); setError("");
    setPhase("ai-speaking");
    setTimeout(() => {
      speakText(next.text, () => {
        setHighlightWordIndex(-1);
        setPhase("listening");
      }, data.questionAudioBase64);
    }, 300);
  }

  const handleCodeSubmit = async () => {
    setShowCodeModal(false);
    setThinkingMsg(thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)]);
    setPhase("preparing-next");
    setLoading(true); setError("");
    try {
      const { data } = await apiClient.post("/mock-interview/answer", { mockInterviewId, answer, code });
      handleApiResponse(data);
    } catch { setError("Failed to submit answer. Please try again."); setPhase("ai-speaking"); }
    setLoading(false);
  };

  /* --- end interview --- */
  const handleEndInterview = async () => {
    setShowEndConfirm(false);
    setEnding(true);
    window.speechSynthesis?.cancel();
    setHighlightWordIndex(-1);
    try {
      await apiClient.post("/mock-interview/end", { mockInterviewId });
      setPhase("complete");
      setInterviewEnded(true);
      setTimeout(() => navigate(`/s/mock-interview/summary/${mockInterviewId}`, { replace: true }), 1500);
    } catch {
      setError("Failed to end interview.");
      setEnding(false);
    }
  };

  /* --- typed answer submit (fallback to voice) --- */
  const handleTypedSubmit = async () => {
    if (!typedAnswer.trim()) return;
    window.speechSynthesis?.cancel();
    setHighlightWordIndex(-1);
    setThinkingMsg(thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)]);
    setPhase("preparing-next");
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.post("/mock-interview/answer", {
        mockInterviewId,
        answer: typedAnswer.trim(),
        code: "",
      });
      setTypedAnswer("");
      handleApiResponse(data);
    } catch {
      setError("Failed to submit answer. Please try again.");
      setPhase("ai-speaking");
    }
    setLoading(false);
  };

  /* ---------- timer helpers ---------- */
  const timerPercent = ((totalSeconds - secondsLeft) / totalSeconds) * 100;
  const isLowTime = secondsLeft <= 60 && secondsLeft > 0;
  const isActivePhase = phase !== "complete" && !interviewEnded;

  const userName = currentUser?.name || "Student";
  const questionProgress = (progress.current / progress.total) * 100;

  /* ---- GUARD: interview already completed ---- */
  if (interviewEnded && phase === "complete") {
    return (
      <div className="mis-session-layout">
        <div className="mis-session-completed-guard">
          <ShieldCheck size={40} className="mis-session-completed-guard__icon" />
          <h3>Interview Completed</h3>
          <p>This interview session has already ended. You can view your results on the summary page.</p>
          <AppButton onClick={() => navigate(`/s/mock-interview/summary/${mockInterviewId}`, { replace: true })}>
            View Summary <ChevronRight size={14} />
          </AppButton>
        </div>
      </div>
    );
  }

  /* ========== render ========== */
  return (
    <div className="mis-session-layout">
      {/* ──── Top bar ──── */}
      <div className="mis-session-topbar">
        <div className="mis-session-topbar__left">
          <AppBadge className="mis-session-topbar__live">
            <span className="mis-session-topbar__live-dot" />{" "}
            LIVE
          </AppBadge>
          <span className="mis-session-topbar__title">{sessionTitle}</span>
        </div>
        <div className="mis-session-topbar__right">
          <div className={`mis-session-timer${isLowTime ? " mis-session-timer--low" : ""}${timeUp ? " mis-session-timer--up" : ""}`}>
            <Clock size={13} />
            <span>{formatTime(secondsLeft)}</span>
          </div>
          <ProgressBar value={timerPercent} size="sm" className="mis-session-topbar__progress" />
          <span className="mis-session-topbar__count">Q{progress.current}/{progress.total}</span>
        </div>
      </div>

      {/* ──── Main content area ──── */}
      <div className="mis-session-content">
        {/* ── Video section (left) ── */}
        <div className="mis-session-video-section">
          {/* Video grid */}
          <div className="mis-session-video-grid">
            {/* AI Interviewer panel */}
            <div className="mis-session-video-panel">
              <div className="mis-session-video-panel__badge">
                <AppBadge variant="secondary" size="sm">
                  <Bot size={12} /> AI Interviewer
                </AppBadge>
              </div>
              <div className="mis-session-video-panel__content">
                <div className={`mis-session-ai-avatar${phase === "ai-speaking" ? " mis-session-ai-avatar--speaking" : ""}${phase === "preparing-next" ? " mis-session-ai-avatar--thinking" : ""}`}>
                  {phase === "ai-speaking" && (
                    <div className="mis-circle-wave">
                      <span className="mis-circle-wave__ring" />
                      <span className="mis-circle-wave__ring" />
                      <span className="mis-circle-wave__ring" />
                    </div>
                  )}
                  {phase === "preparing-next" && (
                    <div className="mis-circle-wave mis-circle-wave--thinking">
                      <span className="mis-circle-wave__ring" />
                      <span className="mis-circle-wave__ring" />
                    </div>
                  )}
                  <div className="mis-session-ai-avatar__icon">
                    <Bot size={40} />
                  </div>
                  {phase === "ai-speaking" && (
                    <div className="mis-session-ai-avatar__indicator">
                      <Volume2 size={12} />
                    </div>
                  )}
                </div>
                <div className="mis-session-video-panel__info">
                  <p className="mis-session-video-panel__name">AI Interviewer</p>
                  <p className="mis-session-video-panel__status">
                    {getAiPanelStatus(phase)}
                  </p>
                </div>
              </div>
            </div>

            {/* User panel */}
            <div className="mis-session-video-panel mis-session-video-panel--user">
              <div className="mis-session-video-panel__badge">
                <AppBadge variant="secondary" size="sm">
                  <Mic size={12} /> You
                </AppBadge>
              </div>
              <div className="mis-session-video-panel__content">
                <div className="mis-circle-wave mis-circle-wave--green" style={{ position: "relative" }}>
                  {!isMuted && phase === "listening" && (
                    <>
                      <span className="mis-circle-wave__ring" />
                      <span className="mis-circle-wave__ring" />
                      <span className="mis-circle-wave__ring" />
                    </>
                  )}
                  <UserAvatar name={userName} size="lg" />
                </div>
                <div className="mis-session-video-panel__info">
                  <p className="mis-session-video-panel__name">{userName}</p>
                  <p className="mis-session-video-panel__status">
                    {getUserMicStatus(isMuted, phase)}
                  </p>
                </div>
              </div>
              {/* Mic label */}
              {micLabel && (
                <div className="mis-session-mic-label">
                  <Mic size={10} />
                  <span>{micLabel.length > 25 ? micLabel.substring(0, 25) + "..." : micLabel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Controls bar */}
          <div className="mis-session-controls">
            <button
              type="button"
              className={`mis-session-controls__btn${isMuted ? " mis-session-controls__btn--danger" : ""}`}
              onClick={() => setIsMuted((m) => !m)}
              aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
            >
              {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <button
              type="button"
              className="mis-session-controls__btn mis-session-controls__btn--end"
              onClick={() => setShowEndConfirm(true)}
              disabled={ending || !isActivePhase}
              aria-label="End interview"
            >
              {ending ? <Spinner size="sm" animation="border" /> : <PhoneOff size={18} />}
            </button>
            <Separator orientation="vertical" className="mis-session-controls__sep" />
            <button
              type="button"
              className={`mis-session-controls__btn mis-session-controls__btn--toggle${showQAPanel ? " mis-session-controls__btn--active" : ""}`}
              onClick={() => setShowQAPanel((v) => !v)}
            >
              <MessageSquare size={16} />
              <span>Q&A Panel</span>
            </button>
          </div>

          {/* Phase & Feedback cards (below video when Q&A panel is hidden) */}
          {!showQAPanel && (
            <div className="mis-session-inline-info">
              {/* Current question */}
              <AppCard className="mis-session-card--question">
                <AppCardContent>
                  <div className="mis-session-question">
                    <div className="mis-session-question__header">
                      <MessageSquare size={16} />
                      <span>Question {progress.current} of {progress.total}</span>
                    </div>
                    <p className="mis-session-question__text">
                      <HighlightedText
                        text={question?.text || "Waiting for question..."}
                        highlightIndex={phase === "ai-speaking" ? highlightWordIndex : -1}
                      />
                    </p>
                  </div>
                </AppCardContent>
              </AppCard>
              {/* Phase indicator */}
              {phase === "listening" && (
                <div className="mis-session-phase-inline">
                  <div className="mis-session-listening-indicator">
                    <span /><span /><span /><span /><span />
                  </div>
                  <p>Speak your answer now...</p>
                </div>
              )}
              {phase === "confirm-transcript" && (
                <div className="mis-session-transcript-confirm">
                  <p className="mis-session-transcript-label">Your answer (voice transcription):</p>
                  <textarea
                    className="mis-session-transcript-textarea"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    rows={4}
                  />
                  <div className="mis-session-transcript-actions">
                    <AppButton size="sm" onClick={submitConfirmedTranscript} disabled={!transcript.trim() || loading}>
                      <CheckCircle size={14} /> Confirm & Submit
                    </AppButton>
                    <AppButton size="sm" variant="secondary" onClick={handleReRecord}>
                      <Mic size={14} /> Re-record
                    </AppButton>
                  </div>
                </div>
              )}
              {(phase === "preparing-next" || phase === "updating-answer" || phase === "waiting-feedback") && (
                <div className="mis-session-phase-inline mis-session-phase-inline--thinking">
                  <div className="mis-session-thinking-dots">
                    <span /><span /><span />
                  </div>
                  <p>{thinkingMsg || "Preparing the next question..."}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Q&A Side Panel (right) ── */}
        {showQAPanel && (
          <div className="mis-session-qa-panel">
            {/* Panel header */}
            <div className="mis-session-qa-panel__header">
              <div className="mis-session-qa-panel__header-top">
                <h3 className="mis-session-qa-panel__title">
                  Question {progress.current} of {progress.total}
                </h3>
                {phase === "listening" && (
                  <AppBadge variant="outline" size="sm">Listening</AppBadge>
                )}
                {(phase === "preparing-next" || phase === "updating-answer" || phase === "waiting-feedback") && (
                  <AppBadge variant="outline" size="sm">Thinking...</AppBadge>
                )}
              </div>
              <ProgressBar value={questionProgress} size="sm" />
            </div>

            {/* Panel body — scrollable */}
            <div className="mis-session-qa-panel__body">
              {/* AI question message */}
              <div className="mis-session-qa-msg">
                <div className="mis-session-qa-msg__avatar">
                  <Bot size={14} />
                </div>
                <div className="mis-session-qa-msg__content">
                  <p className="mis-session-qa-msg__sender">AI Interviewer</p>
                  <p className="mis-session-qa-msg__text">
                    <HighlightedText
                      text={question?.text || "Waiting for question..."}
                      highlightIndex={phase === "ai-speaking" ? highlightWordIndex : -1}
                    />
                  </p>
                </div>
              </div>

              {/* Voice transcript (if available) */}
              {answer && (
                <div className="mis-session-qa-msg mis-session-qa-msg--user">
                  <UserAvatar name={userName} size="sm" />
                  <div className="mis-session-qa-msg__content">
                    <p className="mis-session-qa-msg__sender">Your Answer (voice)</p>
                    <div className="mis-session-qa-msg__answer-box">{answer}</div>
                    <div className="mis-session-qa-msg__recorded">
                      <CheckCircle size={12} />
                      <span>Answer recorded</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Phase indicators */}
              {phase === "listening" && (
                <div className="mis-session-qa-phase">
                  <div className="mis-session-listening-indicator">
                    <span /><span /><span /><span /><span />
                  </div>
                  <p>Speak your answer now...</p>
                  <div className="mis-session-done-speaking">
                    <AppButton size="sm" variant="secondary" onClick={() => {
                      // Signal the auto recorder to stop immediately
                      window.dispatchEvent(new CustomEvent('mock-interview-done-speaking'));
                    }}>
                      <CheckCircle size={14} /> I'm done speaking
                    </AppButton>
                    <span className="mis-session-done-speaking__hint">Or pause for 3 seconds to auto-submit</span>
                  </div>
                </div>
              )}
              {phase === "confirm-transcript" && (
                <div className="mis-session-qa-phase">
                  <div className="mis-session-transcript-confirm">
                    <p className="mis-session-transcript-label">Your answer (voice transcription):</p>
                    <textarea
                      className="mis-session-transcript-textarea"
                      value={transcript}
                      onChange={(e) => setTranscript(e.target.value)}
                      rows={4}
                    />
                    <div className="mis-session-transcript-actions">
                      <AppButton size="sm" onClick={submitConfirmedTranscript} disabled={!transcript.trim() || loading}>
                        <CheckCircle size={14} /> Confirm & Submit
                      </AppButton>
                      <AppButton size="sm" variant="secondary" onClick={handleReRecord}>
                        <Mic size={14} /> Re-record
                      </AppButton>
                    </div>
                  </div>
                </div>
              )}
              {(phase === "preparing-next" || phase === "updating-answer" || phase === "waiting-feedback") && (
                <div className="mis-session-qa-phase mis-session-qa-phase--thinking">
                  <div className="mis-session-thinking-dots">
                    <span /><span /><span />
                  </div>
                  <p>{thinkingMsg || "Preparing the next question..."}</p>
                </div>
              )}
              {phase === "complete" && (
                <div className="mis-session-qa-phase mis-session-qa-phase--complete">
                  <CheckCircle2 size={24} />
                  <p>Interview Complete!</p>
                  <span>Redirecting to your summary...</span>
                </div>
              )}

              {/* Error / Time up */}
              {error && (
                <div className="mis-session-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}
              {timeUp && phase !== "complete" && (
                <div className="mis-session-timeup">
                  <AlertCircle size={14} />
                  <span>Time is up! Your interview will end soon.</span>
                </div>
              )}
            </div>

            {/* Panel footer — typed answer input */}
            <div className="mis-session-qa-panel__footer">
              {phase !== "complete" && !interviewEnded && (
                <>
                  <textarea
                    className="mis-session-qa-panel__textarea"
                    placeholder="Type your answer here (or speak into your microphone)..."
                    value={typedAnswer}
                    onChange={(e) => setTypedAnswer(e.target.value)}
                    rows={3}
                  />
                  <AppButton
                    className="mis-session-qa-panel__submit"
                    size="sm"
                    disabled={!typedAnswer.trim() || loading || phase === "ai-speaking"}
                    onClick={handleTypedSubmit}
                  >
                    {loading ? (
                      <><Spinner size="sm" animation="border" /> Submitting...</>
                    ) : (
                      <><CheckCircle size={14} /> Submit Answer</>
                    )}
                  </AppButton>
                </>
              )}
              {isActivePhase && (
                <AppButton
                  variant="destructive"
                  size="sm"
                  className="mis-session-qa-panel__finish"
                  onClick={() => setShowEndConfirm(true)}
                  disabled={ending}
                >
                  <LogOut size={14} /> End Interview
                </AppButton>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ──── End confirm overlay ──── */}
      {showEndConfirm && (
        <div className="mis-code-overlay">
          <div className="mis-end-confirm">
            <LogOut size={28} className="mis-end-confirm__icon" />
            <h4>End Interview?</h4>
            <p>Are you sure you want to end this interview early? Your progress so far will be saved and scored.</p>
            <div className="mis-end-confirm__actions">
              <AppButton variant="secondary" onClick={() => setShowEndConfirm(false)}>
                Cancel
              </AppButton>
              <AppButton variant="destructive" onClick={handleEndInterview}>
                <LogOut size={14} /> Yes, End Interview
              </AppButton>
            </div>
          </div>
        </div>
      )}

      {/* ──── Code modal overlay ──── */}
      {showCodeModal && (
        <div className="mis-code-overlay">
          <div className="mis-code-modal">
            <div className="mis-code-modal__header">
              <div className="mis-code-modal__title">
                <Code2 size={18} /> Enter Your Code
              </div>
              <button type="button" className="mis-code-modal__close" onClick={() => setShowCodeModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mis-code-modal__body">
              <div className="mis-code-modal__question">
                <strong>Question:</strong>
                <p>{question.text}</p>
              </div>
              <div className="mis-code-modal__editor">
                <Editor
                  height="220px"
                  defaultLanguage="javascript"
                  value={code}
                  onChange={(v) => setCode(v)}
                  theme="vs-dark"
                  options={{ fontSize: 14, minimap: { enabled: false }, padding: { top: 12 } }}
                />
              </div>
              <div className="mis-code-modal__explain">
                <label htmlFor="mis-code-explain">Explain your code (optional):</label>
                <textarea
                  id="mis-code-explain"
                  className="mis-code-modal__textarea"
                  rows={3}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Explain what your code does..."
                />
              </div>
            </div>
            <div className="mis-code-modal__footer">
              <AppButton onClick={handleCodeSubmit}>
                <Send size={14} /> Submit & Continue
              </AppButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterviewSession;
